import React, {useState} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectHexes,
  selectPendingMeleeDamage,
  selectPlayers,
  selectSelectedUnit,
  selectSpecialBuildings,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {getUnitById} from "../../../utils/unit/getUnitById";
import {getPlayerById} from "../../../utils/players/getPlayerById";
import DamageDice from "../DamageDice/DamageDice";
import {calculateMeleeCombatDamage, showMeleeTargetsForPrioritySelect, showToast} from "../../../redux/actions";
import {
  buildTextRerollDiceCountRequired,
  TEXT_ALREADY_REROLLED,
  TEXT_CHOOSE_DICES_TO_REROLL,
  TEXT_CHOOSE_MELEE_PRIORITY_TARGET,
  TEXT_CHOOSE_MELEE_PRIORITY_TARGET_NO_DAMAGE,
  TEXT_CHOOSE_MELEE_PRIORITY_TARGET_ONLY_ONE_TARGET,
  TEXT_UNIT_ALREADY_HAS_BEST_DAMAGE_RESULT,
  TEXT_UNIT_HAS_NO_ADDITIONAL_SUPPLY
} from "../../../constants/text/combat";
import {getDamageDiceRoll} from "../../../utils/dice/getDamageDiceRoll";
import {getBotD6MinRoll} from "../../../utils/dice/getBotD6MinRoll";
import {getBotD6RollBonus} from "../../../utils/dice/getBotD6RollBonus";
import {getLowRatingAdvantageDicePenalty} from "../../../utils/dice/getLowRatingAdvantageDicePenalty";
import {getPlayersByIds} from "../../../utils/players/getPlayersByIds";
import RerollButton from "../RerollButton/RerollButton";
import DamageBonus from "../DamageBonus/DamageBonus";
import {calcMeleeCombatInput} from "../../../utils/gameState/melee/calcMeleeCombatInput";
import {checkIfAlreadyBestMeleeCombatDamage} from "../../../utils/melee/checkIfAlreadyBestMeleeCombatDamage";
import {calcMeleeDamage} from "../../../utils/damage/calcMeleeDamage";
import {selectUserId} from "../../../redux/reducers/privateStateReducer/selectors";
import {checkIfCurrentUserOwner} from "../../../utils/gameState/user/checkIfCurrentUserOwner";
import {checkTutorialActionWouldApply} from "../../../utils/tutorial/tutorialActionGate";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {
  getTutorialRequiredMeleeRerollDiceCount,
  resolveTutorialRerollDiceCount
} from "../../../utils/tutorial/tutorialActionGate";
import {
  getMeleeCountedDiceIndexes,
  getMeleeCountedDiceRolls,
  getMeleeDiceRolls
} from "../../../utils/melee/getMeleeDiceRolls";
import {MELEE_COUNTED_DAMAGE_DICE_COUNT} from "../../../configs/damage";
import {WAR_COLOR} from "../../../styles/constants";
import {
  CombatControls,
  CombatDamagePlaque,
  CombatDiceGroup,
  CombatDuel,
  CombatEquation,
  CombatOperator,
  CombatPanel,
  CombatTerm,
  CombatTotal,
  CombatUnitToken,
  getCombatTargetSizeProps,
  getCombatUnitIconProps,
} from "../CombatPanel/CombatPanel";

const NO_DICE_SELECTED = [];

// Кости в формате записи боя: diceRoll3/diceRoll4 — только у отряда с 4
// костями (селестиалы, см. getMeleeDiceRolls)
const toMeleeDiceEntry = (diceRolls) => ({
  diceRoll1: diceRolls[0],
  diceRoll2: diceRolls[1],
  ...(diceRolls.length > MELEE_COUNTED_DAMAGE_DICE_COUNT ? {diceRoll3: diceRolls[2], diceRoll4: diceRolls[3]} : {}),
});

const SelectedUnitMelee = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const hexes = useSelector(selectHexes);
  const selectedUnit = useSelector(selectSelectedUnit);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const players = useSelector(selectPlayers);
  const pendingMeleeDamage = useSelector(selectPendingMeleeDamage);
  const units = useSelector(selectUnits);
  const userId = useSelector(selectUserId);
  const citiesCache = useSelector(selectCitiesCache);
  const specialBuildings = useSelector(selectSpecialBuildings);
  const buildingsDamage = useSelector(selectBuildingsDamage);
  const tutorialRequiredRerollDiceCount = useSelector(getTutorialRequiredMeleeRerollDiceCount);

  // Выбор костей на переброс по их порядку (у селестиалов 4 кости)
  const [diceSelected, setDiceSelected] = useState(NO_DICE_SELECTED);
  const [shouldBounceDices, setShouldBounceDices] = useState(false);

  if (!selectedUnit) return null;

  const meleeAction = pendingMeleeDamage.find(action => action.unitId === selectedUnit.id);
  if (!meleeAction) return null;

  const {hasRerolled, diceRoll1, diceRoll2, damage, bonus, damageDistribution} = meleeAction;
  const diceRolls = getMeleeDiceRolls(meleeAction);
  // Отряд бросает больше костей, чем идет в урон (селестиалы): учтенные
  // отмечены, остальные приглушены
  const hasExtraDice = diceRolls.length > MELEE_COUNTED_DAMAGE_DICE_COUNT;
  const countedDiceIndexes = getMeleeCountedDiceIndexes(diceRolls);
  const countedDiceRolls = getMeleeCountedDiceRolls(meleeAction);
  const checkIfDiceSelected = (index) => Boolean(diceSelected[index]);
  const setDiceSelectedAt = (index) => (isSelected) => setDiceSelected((prev) => {
    const next = [...prev];
    next[index] = isSelected;
    return next;
  });

  const isCurrentUserOwner = checkIfCurrentUserOwner(userId, selectedUnit.ownerId);
  const owner = getPlayerById(players, selectedUnit.ownerId);
  const targetEntries = Object.entries(damageDistribution);

  const isAlreadyBestRollResult = checkIfAlreadyBestMeleeCombatDamage(...countedDiceRolls);
  const isRerollDisabled = hasRerolled || !selectedUnit.additionalSupply || isAlreadyBestRollResult;

  const handleRerollClick = (onReroll) => {
    if (hasRerolled) {
      dispatch(showToast(TEXT_ALREADY_REROLLED, 'warning', 3000));
      return;
    }

    if (!selectedUnit.additionalSupply) {
      dispatch(showToast(TEXT_UNIT_HAS_NO_ADDITIONAL_SUPPLY, 'warning', 3000));
      return;
    }

    if (isAlreadyBestRollResult) {
      dispatch(showToast(TEXT_UNIT_ALREADY_HAS_BEST_DAMAGE_RESULT, 'success', 3000))
      return;
    }

    const selectedDiceCount = diceRolls.filter((_, index) => checkIfDiceSelected(index)).length;
    if (selectedDiceCount === 0) {
      dispatch(showToast(TEXT_CHOOSE_DICES_TO_REROLL, 'info', 3000));
      setShouldBounceDices(true);
      setTimeout(() => setShouldBounceDices(false), 400);
      return;
    }

    // Обучение ждет переброса определенного числа костей (глава 3 — обеих):
    // переброс меньшего гейт обучения все равно не пропустит, а без этой
    // проверки звук броска прозвучал бы впустую, а выбор костей сбросился бы.
    // Кость с +1 выбрать нельзя — она в требование не входит
    const requiredDiceCount = resolveTutorialRerollDiceCount(tutorialRequiredRerollDiceCount, diceRoll1, diceRoll2);
    if (requiredDiceCount && selectedDiceCount < requiredDiceCount) {
      dispatch(showToast(buildTextRerollDiceCountRequired(requiredDiceCount, selectedDiceCount), 'warning', 4000));
      setShouldBounceDices(true);
      setTimeout(() => setShouldBounceDices(false), 400);
      return;
    }

    // Кости принадлежат владельцу отряда (см. calcMeleeCombatInput) — у живых
    // игроков подкруток нет. Кнопка достается и админу, управляющему ботом
    // (см. checkIfCurrentUserOwner): тогда при нем остаются пол и прибавка
    // уровня бонуса бота (пол против игрока под давлением здесь не учитывается),
    // если в ЭТОМ бою против него нет бота (см. checkIfAnyOpponentIsBot).
    // Остается при нем и штраф форы новичкам:
    // противники в этом бою — те, между кем распределяется урон отряда; если
    // среди них игрок с форой, бот и перебрасывает со штрафом к грани (см.
    // getLowRatingAdvantageDicePenalty)
    const combatEnemyOwnerIds = units
      .filter((unit) => Object.prototype.hasOwnProperty.call(damageDistribution || {}, unit.id))
      .map((unit) => unit.ownerId);
    const combatEnemyOwners = getPlayersByIds(players, combatEnemyOwnerIds);
    const rerollMinRoll = getBotD6MinRoll(owner, combatEnemyOwners);
    const rerollBonus = getBotD6RollBonus(owner, combatEnemyOwners);
    const rerollFacePenalty = getLowRatingAdvantageDicePenalty(owner, combatEnemyOwners);
    const newDiceEntry = toMeleeDiceEntry(diceRolls.map((diceRoll, index) => (
      checkIfDiceSelected(index)
        ? getDamageDiceRoll(rerollMinRoll, rerollBonus, rerollFacePenalty)
        : diceRoll
    )));
    const damageToChange = {
      [selectedUnit.id]: newDiceEntry,
    }
    const meleeCombatInput = calcMeleeCombatInput(
      units, currentPlayer.id, pendingMeleeDamage, damageToChange, players
    );

    const meleeContext = {
      owner, citiesCache, specialBuildings, buildingsDamage, hexes, units, players
    }
    const {damage: prevDamage} = calcMeleeDamage(selectedUnit, ...countedDiceRolls, meleeContext);
    const {damage: newDamage} = calcMeleeDamage(
      selectedUnit, ...getMeleeCountedDiceRolls(newDiceEntry), meleeContext
    );
    const isBadLuck = newDamage <= prevDamage;

    const diceSelectedFlags = diceRolls.map((_, index) => checkIfDiceSelected(index));
    const rerollAction = calculateMeleeCombatDamage(
      meleeCombatInput, selectedUnit.id, undefined, true, isBadLuck, ...diceSelectedFlags
    );
    // Шаг обучения переброс пока не разрешает (например, сначала изучить оба
    // отряда боя): гейт заблокирует действие и покажет задание, а звук броска
    // прозвучал бы впустую — выбор костей остается до разрешенного шага
    if (!checkTutorialActionWouldApply(store.getState(), rerollAction)) {
      dispatch(rerollAction);
      return;
    }
    dispatch(rerollAction);

    // Звук переброса различает одну кость и несколько
    onReroll(selectedDiceCount > 0, selectedDiceCount > 1, isBadLuck);

    setDiceSelected(NO_DICE_SELECTED);
  }


  const isNoDamage = damage <= 0;
  const isOnlyOneTarget = targetEntries.length <= 1;
  const isPriorityTargetDisabled = isNoDamage || isOnlyOneTarget;

  const handleChoosePriorityClick = () => {
    if (isNoDamage) {
      dispatch(showToast(TEXT_CHOOSE_MELEE_PRIORITY_TARGET_NO_DAMAGE, 'warning'));
      return;
    }
    if (isOnlyOneTarget) {
      dispatch(showToast(TEXT_CHOOSE_MELEE_PRIORITY_TARGET_ONLY_ONE_TARGET, 'warning'));
      return;
    }

    REGISTRY_SINGLETON.getSoundManager().playMenuButtonClick();
    dispatch(showMeleeTargetsForPrioritySelect());
    dispatch(showToast(TEXT_CHOOSE_MELEE_PRIORITY_TARGET, 'info'));
  }

  const renderDice = () => diceRolls.map((diceRoll, index) => (
    <React.Fragment key={`melee-dice-${selectedUnit.id}-${index + 1}`}>
      {index > 0 && <CombatOperator>{hasExtraDice ? ',' : '+'}</CombatOperator>}
      <DamageDice
        ownerId={selectedUnit.ownerId}
        hasAdditionalSupply={selectedUnit.additionalSupply}
        hasRerolled={hasRerolled}
        damage={diceRoll}
        isAccounted={hasExtraDice && countedDiceIndexes.includes(index)}
        isDiscarded={hasExtraDice && !countedDiceIndexes.includes(index)}
        isSelected={checkIfDiceSelected(index)}
        setSelected={setDiceSelectedAt(index)}
        shouldBounce={shouldBounceDices}
      />
    </React.Fragment>
  ));

  const targetSizeProps = getCombatTargetSizeProps(targetEntries.length);
  const targets = targetEntries.map(([targetUnitId, targetDamage]) => {
    const targetUnit = getUnitById(units, targetUnitId);
    if (!targetUnit) return null;
    return (
      <CombatUnitToken
        key={targetUnitId}
        unitType={targetUnit.type}
        iconProps={getCombatUnitIconProps(targetUnit, players)}
        isTarget={true}
        plaque={<CombatDamagePlaque damage={targetDamage}/>}
        {...targetSizeProps}
      />
    );
  });

  return (
    <CombatPanel
      key={selectedUnit.id}
      toneColor={WAR_COLOR}
      data-tutorial-melee-owner={isCurrentUserOwner ? 'own' : 'enemy'}
      duel={(
        <CombatDuel
          actor={<CombatUnitToken unitType={selectedUnit.type} iconProps={getCombatUnitIconProps(selectedUnit, players)}/>}
          actionIcon={<img src={'/img/icons/barracks red.png'} alt={'Ближний бой'} title={'Ближний бой'}/>}
          targets={targets}
        />
      )}
    >
      <CombatEquation className={'melee-toolbar_stats-container'}>
        <CombatTerm value={selectedUnit.size} icon={'/img/icons/unit size.png'} iconAlt={'unit size'} title={'Размер отряда'}/>
        <CombatOperator>{'+'}</CombatOperator>
        <CombatDiceGroup
          // Подпись над группой, а не в строке формулы: 4 кости и так едва
          // помещаются в футер на телефоне
          caption={hasExtraDice ? `${MELEE_COUNTED_DAMAGE_DICE_COUNT} лучшие из ${diceRolls.length}` : null}
          title={hasExtraDice ? `В урон идут ${MELEE_COUNTED_DAMAGE_DICE_COUNT} лучшие кости из ${diceRolls.length}` : undefined}
        >
          {renderDice()}
        </CombatDiceGroup>
        <DamageBonus bonus={bonus}/>
      </CombatEquation>

      <CombatTotal value={damage} isDamage={true}/>

      {isCurrentUserOwner && (
        <CombatControls>
          {targetEntries.length > 0 && (
            <button
              className={`city-capture-modal-button melee-toolbar_priority-button ${
                isPriorityTargetDisabled ? 'gui-disabled-look' : ''
              }`}
              onClick={handleChoosePriorityClick}
              title={'Приоритет'}
            >
              <h2 className={'combat-controls_priority-text'}>Приоритет</h2>
              <img
                className={'combat-controls_icon combat-controls_priority-icon'}
                src={'/img/icons/priority_target.png'}
                alt={'Приоритет'}
              />
            </button>
          )}

          <RerollButton
            handleRerollClick={handleRerollClick}
            isDisabledStyle={isRerollDisabled}
          />
        </CombatControls>
      )}
    </CombatPanel>
  );
};

export default SelectedUnitMelee;
